<template>
  <div class="chat-box">
    <div class="chat-box-header">
      <div>{{ $t("bnpl:chat.header_text") }}</div>
      <div v-if="isFeedbackOpen" class="user-feedback">
        <div class="user-feedback-question">
          <div>{{ $t("bnpl:chat.user_feedback.question") }}</div>
          <IconsClose class="close-button" @click="closeFeedback"/>
        </div>
        <div class="user-feedback-answers">
          <div class="user-feedback-answer" @click="sendUserFeedback($t('bnpl:chat.user_feedback.negative_answer'))">{{ $t("bnpl:chat.user_feedback.negative_answer") }}</div>
          <div class="user-feedback-answer" @click="sendUserFeedback($t('bnpl:chat.user_feedback.positive_answer'))">{{ $t("bnpl:chat.user_feedback.positive_answer") }}</div>
          <!-- <div class="user-feedback-answer" @click="sendUserFeedback($t('bnpl:chat.user_feedback.negative_answer'))">no</div>
          <div class="user-feedback-answer" @click="sendUserFeedback($t('bnpl:chat.user_feedback.positive_answer'))">yes</div> -->
        </div>
      </div>
    </div>
    <div class="chat-history">
      <div v-for="message in messageHistory" class="message" :class="{user: message.sentBy === 'USER'}">
        <div v-if="message.sentBy === 'BOT'" class="message-header">
          <div class="message-header-avatar">
            <img src="~/assets/img/nova_256.jpg" />
          </div>
          <div class="message-header-details">
            <span class="message-header-details-name">{{ $t("bnpl:chat.bot_name") }}</span>
            <span class="message-header-details-info">{{ $t("bnpl:chat.from_instacash") }}</span>
          </div>
        </div>
        <div class="message-content">
          {{ message.message }}
        </div>
        <div v-if="message.createdAt === 'sending...'" class="message-footer">
          {{ $t("bnpl:chat.indicator.sending") }}
        </div>
        <div v-else class="message-footer">
          {{ $d(message.createdAt, "longDateTime") }}
        </div>
      </div>
    </div>
    <div v-if="responseOptions.length > 0" class="response-options">
      <button v-for="responseOption in responseOptions" :key="responseOption.messageId" class="response-option" @click="sendMessage(responseOption)">
        {{ responseOption.message }}
      </button>
    </div>
    <div v-if="(responseOptions.length === 0 && terminal === 'MESSENGER' && isMessengerOpen) && !chatStore.isAiRunning">
      <div class="messenger flex-row align-items-center padding0 gap24 px-3">
        <IconsClose class="messenger-close-button" @click="closeMessenger"/>
        <div style="width:80px; height:80px;">
          <IconsFacebookMessenger />
        </div>
        <div class="flex-column align-items-start justify-content-center padding0 gap4 grow">
          <p class="p-caption bold gray600">
            {{ $t("bnpl:chat_on_facebook_messenger") }}
          </p>
          <a class="btn btnM stretch" target="_blank" :href="`https://m.me/${countryStore.messengerPageId}`">
            {{ $t("bnpl:chat_on_facebook_messenger_button") }}
          </a>
        </div>
      </div>
    </div>
    <div v-if="responseOptions.length === 0" class="chat-box-footer">
      <div v-if="chatStore.isAiRunning" class="typing-indicator">
        {{ $t("bnpl:chat.typing_indicator") }}
      </div>
      <ChatOptionsPopup v-if="isOptionsOpen" :flowPosition="flowPosition" @click="showGeneralOptions" />
      <IconsAddFill class="general-chat-icon" @click="showGeneralOptions" />
      <label class="user-chat-input">
        <input v-model="userInput" @keypress.enter="sendUserInputMessage"/>
      </label>
      <IconsSend class="send-icon" @click="sendUserInputMessage"/>
    </div>
  </div>
</template>

<script setup>
import { useChatStore } from '~~/stores/chat';
import { useCountryStore } from '~~/stores/country';
// import { formatDateTime } from '~~/utils/util';

const route = useRoute();

const chatStore = useChatStore();
const countryStore = useCountryStore();

const props = defineProps({
  flowPosition: {
    type: String,
    required: true,
  }
})

const responseOptions = computed(() => chatStore.getResponseOptions);
const messageHistory = computed(() => [...chatStore.getMessageHistory].reverse());
const terminal = computed(() => chatStore.terminal);

const isOpen = ref(false);
const isFocused = ref(false);
const isOptionsOpen = ref(false);
const isMessengerOpen = ref(true);
const isFeedbackOpen = ref(false);
const hideInput = ref(false);
const forceMessengerOpen = ref(false);
const userInputMessage = ref("");
let sendDelayTimeout = null;
onBeforeUnmount(() => clearTimeout(sendDelayTimeout));

const userInput = ref(null);

function showGeneralOptions() {
  isOptionsOpen.value = !isOptionsOpen.value;
}

function closeMessenger() {
  isMessengerOpen.value = false;
}

function closeFeedback() {
  isFeedbackOpen.value = false;
}

async function sendUserInputMessage() {
  if (userInput.value.trim()) {
    if (getRunCount() >= 10) {
      forceMessengerOpen.value = true;
      hideInput.value = true;
      return;
    }
    userInputMessage.value = userInputMessage.value + "\n" + userInput.value;
    chatStore.history.push({
      id: chatStore.history.length,
      message: userInput.value,
      sentBy: "USER",
      createdAt: "sending...",
    });
    userInput.value = null;
    clearTimeout(sendDelayTimeout);
    sendDelayTimeout = setTimeout(async () => {
      userInput.value = null;
      try {
        await chatStore.userInputAiChat({
          publicId: route.params.publicId,
          flowPosition: chatStore.flowPosition,
          locale: countryStore.shortLocale,
          message: userInputMessage.value,
        });
        userInputMessage.value = "";
        increaseRunCount();
      } catch (e2) {
        console.debug(e2);
      }
    }, 5000);
  }
}

function increaseRunCount() {
  const runCount = getRunCount();
  sessionStorage.setItem(`chat-run-count-${route.params.publicId ?? route.params.prescoreId}`, runCount + 1); 
}

function getRunCount() {
  return parseInt(sessionStorage.getItem(`chat-run-count-${route.params.publicId ?? route.params.prescoreId}`)) || 0;
}

async function sendMessage(message) {
  chatStore.history.push({
    id: chatStore.history.length,
    message: message.message,
    messageId: message.messageId,
    sentBy: "USER",
    createdAt: "sending...",
  });
  await chatStore.chat({
    publicId: route.params.publicId ?? route.params.prescoreId,
    flowPosition: chatStore.flowPosition,
    locale: countryStore.shortLocale,
    message: message.message,
    responseId: message.messageId,
    isGeneralChat: chatStore.isGeneralChat,
  })
  isMessengerOpen.value = true;
}

function sendUserFeedback(feedback) {
  chatStore.sendUserFeedback({
    publicId: route.params.publicId ?? route.params.prescoreId,
    flowPosition: chatStore.flowPosition,
    feedback: feedback,
  });
}

watch(
  () => responseOptions.value.length,
  (value) => {
    if (value === 0) {
      isFeedbackOpen.value = true;
      setTimeout(() => {
        isFeedbackOpen.value = false;
      }, 30000)
    }
  }
)

onMounted(() => {
  
})
</script>

<style scoped lang="scss">
  .chat-box {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    height: 100%;

    .chat-box-header {
      font-weight: 700;
      position: relative;

      .user-feedback {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin: 19px -24px 0px -24px;
        font-weight: 400;
        background: white;
        color: var(--black);
        display: flex;
        flex-direction: column;
        box-shadow: 0px 5px 5px 0px #0000001e;
        border-top: 1px solid var(--gray200);

        
        .user-feedback-question {
          padding: 10px 5px 10px 20px;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;

          .close-button {
            width: 25px;
            height: 25px;
            padding: 5px;
            cursor: pointer;
          }
        }

        .user-feedback-answers {
          width: 100%;
          display: flex;
          flex-direction: row;
          background: var(--gray200);

          .user-feedback-answer {
            flex: 1;
            display: flex;
            justify-content: center;
            padding: 10px 20px;
            border-collapse: collapse;
            background: white;
            cursor: pointer;
            overflow: hidden;
            
            &:not(:first-child) {
              margin: 1px 0px 0px 0.5px;
            }
            &:not(:last-child) {
              margin: 1px 0.5px 0px 0px;
            }
            &:hover {
              // backdrop-filter: brightness(50%) blur(0.5em);
              box-shadow: inset 0px 0px 1px 999999px var(--gray50);
              // color: white;
            }
          }
        }
      }
    }

    .chat-box-footer {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
      position: relative;
      padding: 0px;
      margin: 16px -10px -10px -10px;

      .typing-indicator {
        position: absolute;
        top: -28px;
        left: -14px;
        right: -14px;
        padding: 4px 20px;
        font-size: 12px;
        line-height: 12px;
        color: var(--gray400);
        background: var(--brandPrimaryLight);

        &.hidden {
          display: none;
        }
      }

      .general-chat-icon {
        width: 24px;
        height: 38px;
        padding: 7px 0px 7px 0px;
        fill: var(--brandPrimary);
        z-index: 1;
        cursor: pointer;

        &:hover {
          fill: var(--brandPrimaryDark);
        }
      }

      .send-icon {
        width: 24px;
        height: 38px;
        padding: 7px 0px 7px 0px;
        stroke: var(--brandPrimary);
        z-index: 1;
        cursor: pointer;

        &:hover {
          stroke: var(--brandPrimaryDark);
        }
      }

      .user-chat-input {
        flex: 1;
        box-sizing: border-box;
        display: flex;
        flex-direction: row;
        align-items: center;
        border: 1px solid var(--gray400);
        border-radius: 9999999999px;
        font-weight: 400;
        font-size: 16px;
        line-height: 16px;
        height: fit-content;
        cursor: text;
        
        &:hover {
          border: 1px solid var(--brandPrimary);
        }
        
        input {
          padding: 8px 10px 8px 10px;
          border: none;
          border-radius: inherit;
          line-height: 20px;
          width: 100%;

          &:focus-visible {
            outline: none;
          }
        }

        button {
          border: none;
          background: none;
          padding: 9px;
          flex: 1;
          align-self: stretch;
          cursor: pointer;
          
          svg {
            fill: var(--gray400);
            width: 18px;
            height: 18px;
          }
          &:hover {
            svg {
              fill: var(--brandPrimaryDark);
            }
          }
        }
      }
    }
    
    .chat-history {
      display: flex;
      flex-direction: column-reverse;
      gap: 20px;
      height: 100%;
      overflow: auto;
      margin-right: -15px;
      padding-right: 15px;

      &::-webkit-scrollbar {
        width: 5px;
      }

      /* Track */
      &::-webkit-scrollbar-track {
        background: transparent;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: rgba(136, 136, 136, 0.25);
        border-radius: 999999999px;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: rgb(85, 85, 85, 0.5);
      }

      .message {
        display: flex;
        flex-direction: column;
        background: var(--gray50);
        max-width: 80%;
        width: fit-content;
        border-radius: 6px;
        font-size: 13px;
        line-height: 15.6px;
        padding: 10px;
        gap: 8px;

        &.user {
          background: var(--brandPrimaryLight);
          align-self: end;
        }

        .message-header {
          display: flex;
          flex-direction: row;
          align-items: stretch;
          gap: 8px;
          
          .message-header-avatar {
            overflow: hidden;
            width: 32px;
            height: 32px;
            border-radius: 100%;
            
            img {
              display: block;
              height: inherit;
              width: inherit;
            }
          }
          
          .message-header-details {
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;

            .message-header-details-name {
              font-weight: 700;
            }

            .message-header-details-info {
              font-weight: 400;
              color: var(--gray400);
              // margin-left: 0.5ch;
            }
          }
        }

        .message-content {
          margin-left: 40px;
        }

        &.user .message-content {
          margin-left: 0px;
        }

        .message-footer {
          font-size: 11px;
          color: var(--gray400);
          align-self: flex-end;
          margin: 0px -3px -8px 0px;
        }
      }
    }

    .response-options {
      display: flex;
      flex-direction: column;
      align-items: end;
      gap: 10px;

      .response-option {
        background: var(--brandPrimary);
        color: white;
        border: none;
        border-radius: 6px;
        padding: 10px;
        
        &:hover {
          background: var(--brandPrimaryDark);
        }
      }
    }
  }

  .messenger {
    padding: 20px 0px 0px 0px;
    position: relative;

    .messenger-close-button {
      width: 33px;
      height: 33px;
      padding: 10px 10px 5px 5px;
      position: absolute;
      top: 0px;
      right: -20px;
      cursor: pointer;
    }
  }
</style>

<style lang="scss">

</style>